import { useState } from 'react';
import { Building2, ChevronDown, Search } from 'lucide-react';
import { useSupabaseAdmin } from '@/hooks/useSupabaseAdmin';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

/**
 * O que faz: Renderiza o seletor de clínica usado pelo administrador para escolher qual conta visualizar.
 * Onde usa: Barras de modo administrador no Dashboard e no Pipeline.
 * Conexões: Recebe clínica selecionada por props, usa useSupabaseAdmin para listar clínicas e aciona onClinicaSelected.
 * Tabelas: Dados vêm do hook administrativo, incluindo nome, email, status, total_leads, total_usuarios e taxa_conversao.
 * Data: 28/04/2026
 */

interface AdminClinicSelectorProps {
  clinicaSelecionada: any | null;
  onClinicaSelected: (clinica: any | null) => void;
  showStats?: boolean;
}

export const AdminClinicSelector = ({
  clinicaSelecionada,
  onClinicaSelected,
  showStats = true
}: AdminClinicSelectorProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const {
    clinicas,
    loading,
    isAdmin,
    adminCheckLoading
  } = useSupabaseAdmin();

  // Filtra por nome ou email para o administrador encontrar a clínica rapidamente sem navegar por uma lista longa.
  const clinicasFiltradas = clinicas.filter(clinica =>
    clinica.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    clinica.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Centraliza a formatação para manter o mesmo texto no seletor e nos indicadores da barra.
  const taxaConversaoFormatada = `${clinicaSelecionada?.taxa_conversao?.toFixed(1) || '0'}%`;

  // Fecha o menu após escolher a clínica para confirmar visualmente que a seleção foi aplicada.
  const handleClinicaSelect = (clinica: any) => {
    onClinicaSelected(clinica);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Permite voltar ao estado sem clínica quando o administrador precisa comparar ou trocar contexto.
  const handleClearSelection = () => {
    onClinicaSelected(null);
  };

  // Mostra estado de permissão porque o seletor só pode aparecer depois da validação de administrador.
  if (adminCheckLoading) {
    return (
      <div className="flex items-center gap-2 rounded border border-info/30 bg-info-subtle p-2">
        <Building2 className="h-4 w-4 animate-pulse text-info-text" />
        <span className="text-sm text-info-text">Verificando permissões...</span>
      </div>
    );
  }

  // Bloqueia o seletor para usuários comuns porque trocar clínica é uma ação exclusiva do modo administrador.
  if (!isAdmin) {
    return (
      <div className="flex items-center gap-2 rounded border border-destructive/30 bg-danger-subtle p-2">
        <Building2 className="h-4 w-4 text-danger-text" />
        <span className="text-sm text-danger-text">Acesso restrito a administradores</span>
      </div>
    );
  }

  // Mantém feedback visual enquanto as clínicas carregam para evitar uma barra vazia sem explicação.
  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded border border-info/30 bg-info-subtle p-2">
        <Building2 className="h-4 w-4 animate-pulse text-info-text" />
        <span className="text-sm text-info-text">Carregando clínicas...</span>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="h-[46px] w-full max-w-[560px] justify-between rounded-[14px] border-input bg-card px-4 py-2 text-left shadow-none transition-colors hover:bg-muted focus-visible:border-[#9eb959] focus-visible:ring-4 focus-visible:ring-[rgba(184,218,85,0.28)] focus-visible:ring-offset-0 data-[state=open]:border-[#9eb959]"
          >
            <div className="flex min-w-0 items-center gap-3">
              {/* Ícone da clínica em lima névoa com verde profundo (marca), não em azul informativo. */}
              <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-accent">
                <Building2 className="h-4 w-4 text-accent-foreground" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                {clinicaSelecionada ? (
                  <div>
                    <div className="truncate text-sm font-semibold text-foreground">
                      {clinicaSelecionada.nome}
                    </div>
                    <div className="truncate text-xs font-normal text-muted-foreground">
                      {clinicaSelecionada.email}
                    </div>
                  </div>
                ) : (
                  <span className="text-sm font-normal text-muted-foreground">
                    Selecionar clínica para visualizar...
                  </span>
                )}
              </div>
            </div>
            <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          className="w-[min(600px,calc(100vw-2rem))] rounded-2xl p-3 shadow-lg"
          // Impede o Radix de forçar foco no gatilho e interromper a digitação durante o filtro.
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          {/* Busca local evita chamadas extras ao banco enquanto o administrador digita. */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome ou email da clínica..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              // Bloqueia o atalho de navegação interna do menu para cada tecla continuar no campo.
              onKeyDown={(event) => event.stopPropagation()}
              // Mantém foco automático no campo ao abrir para agilizar a busca incremental.
              autoFocus
              className="my-[10px] h-9 rounded-[14px] border-input py-0 pl-9 pr-3 focus-visible:border-[#9eb959] focus-visible:ring-4 focus-visible:ring-[rgba(184,218,85,0.28)] focus-visible:ring-offset-0"
            />
          </div>

          {/* Limpa a seleção para permitir que páginas admin voltem ao estado vazio controlado. */}
          {clinicaSelecionada && (
            <DropdownMenuItem
              onClick={handleClearSelection}
              className="my-[10px] mb-2 rounded-xl border-b px-[20px] py-2 text-muted-foreground hover:bg-muted"
            >
              <Building2 className="mr-2 h-4 w-4" />
              Visualizar todas as clínicas
            </DropdownMenuItem>
          )}

          {/* Lista mais alta (até 60% da tela) para mostrar várias clínicas de uma vez; antes cabiam só ~3.
              O fade nas bordas (crm-scroll-fade, padrão do AGENTS.md) indica que há mais itens ao rolar. */}
          <div className="crm-scroll crm-scroll-fade max-h-[min(60vh,560px)] space-y-1 overflow-y-auto pr-1">
            {clinicasFiltradas.length > 0 ? (
              clinicasFiltradas.map(clinica => (
                <DropdownMenuItem
                  key={clinica.id}
                  onClick={() => handleClinicaSelect(clinica)}
                  className="my-[6px] cursor-pointer rounded-xl border border-border px-[20px] py-[12px] transition-colors hover:bg-muted data-[highlighted]:bg-accent aria-selected:bg-accent"
                >
                  <div className="flex w-full items-center justify-between">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <Building2 className="h-4 w-4 flex-shrink-0 text-accent-foreground" aria-hidden="true" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-foreground">
                          {clinica.nome}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {clinica.email}
                        </div>
                      </div>
                    </div>
                    {/* Só a taxa de conversão fica no item; leads e usuários saíram a pedido para a lista ficar mais limpa. */}
                    {showStats && clinica.taxa_conversao !== undefined && (
                      <div className="ml-4 flex items-center gap-1 text-xs text-muted-foreground">
                        <div className="h-2 w-2 rounded-full bg-accent-foreground"></div>
                        <span className="font-medium">{clinica.taxa_conversao.toFixed(1)}%</span>
                      </div>
                    )}

                    {/* Status visível evita selecionar por engano uma clínica inativa ou em configuração.
                        Usa as cores de status (verde suave), não o lima de ação, que cansava a vista na lista. */}
                    <Badge
                      variant="outline"
                      className={`ml-2 flex-shrink-0 border-transparent text-xs ${(clinica.status || 'ativo') === 'ativo' ? 'bg-success-subtle text-success-text' : 'bg-secondary text-muted-foreground'}`}
                    >
                      {clinica.status || 'ativo'}
                    </Badge>
                  </div>
                </DropdownMenuItem>
              ))
            ) : (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {searchTerm ? 'Nenhuma clínica encontrada' : 'Nenhuma clínica disponível'}
              </div>
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Resumo da clínica selecionada: só a conversão (leads e usuários foram retirados a pedido). */}
      {clinicaSelecionada && showStats && (
        <div className="flex w-full items-center justify-start gap-6 rounded-[14px] border border-border bg-muted px-4 py-3 text-xs lg:w-auto lg:justify-end">
          <div className="flex items-center gap-2 whitespace-nowrap">
            <div className="h-2 w-2 rounded-full bg-accent-foreground"></div>
            <span className="font-semibold text-accent-foreground">{taxaConversaoFormatada}</span>
            <span className="text-muted-foreground">conversão</span>
          </div>
        </div>
      )}
    </div>
  );
};
